import React, { useCallback, useEffect, useState } from 'react';
import { styled } from '@this/constants/themes';
import moment from 'moment';
import { RevenueReferenceDateTypes } from '@this/src/domain/organization/organization3';
import type Trip from '@this/src/domain/trip/trip2';
import TripStatusIndicator from '../../shared/trip_status_indicator';

interface Props {
  trip: Trip;
}

const ArchiveTrip: React.FC<Props> = ({ trip }) => {
  const [sinceLastUpdate, setSinceLastUpdate] = useState<string>('');
  const [isLongTimeNoUpdate, setIsLongTimeNoUpdate] = useState<boolean>(false);
  const revenueReferenceDateType = trip?.user?.organization?.revenueReferenceDateType;

  const pastTimeInfo = useCallback(() => {
    if (trip.lastActionAt == null && trip.updatedAt == null) {
      return;
    }
    const targetDate = trip.lastActionAt || trip.updatedAt;
    const millisec = moment().diff(moment(targetDate));

    setIsLongTimeNoUpdate(false);
    let lastUpdate = '';
    if (millisec < 1000) {
      lastUpdate = 'たった今';
    } else if (millisec / 1000 < 60) {
      lastUpdate = `${Math.floor(millisec / 1000)}秒前`;
    } else if (millisec / 1000 / 60 < 60) {
      lastUpdate = `${Math.floor(millisec / 1000 / 60)}分前`;
    } else if (millisec / 1000 / 60 / 60 < 24) {
      lastUpdate = `${Math.floor(millisec / 1000 / 60 / 60)}時間前`;
    } else {
      lastUpdate = `${Math.floor(millisec / 1000 / 60 / 60 / 24)}日前`;
      setIsLongTimeNoUpdate(true);
    }
    setSinceLastUpdate(lastUpdate);
  }, [trip]);

  useEffect(() => {
    pastTimeInfo();
  }, [trip]);

  return (
    <Wrapper>
      <Item>
        {trip.existsUnopendUserMessage && (
          <div>
            <New id={`newmark-trip-${trip.id}`}>新着メッセージがあります</New>
          </div>
        )}
        <TripStatusIndicator status={trip.status || 0} />
        <span>{trip.billing ? '<請求書払い>' : '<カード払い>'}</span>
        <div>企業名：{trip?.user?.organization?.name}</div>
        <div>売上基準日：{RevenueReferenceDateTypes[revenueReferenceDateType || 'confirmed_at']}</div>
        <div>
          {trip.user && trip.user.name}
          {` [${trip.id}]`}
        </div>
      </Item>
      <Item>
        <div>{`${trip.cacheStartTime?.format('YYYY/MM/DD')} → ${trip.cacheEndTime?.format('YYYY/MM/DD')}`}</div>
        <div>手配依頼日：{trip.receivedAt?.format('YYYY-MM-DD')}</div>
        <div>
          最終更新：
          <LastUpdateSection isLongTimeNoUpdate={isLongTimeNoUpdate}>{sinceLastUpdate}</LastUpdateSection>
        </div>
      </Item>
      <Item>合計: {utils.formatPrice(trip.totalPrice + trip.nonOrderItemsTotalPrice)}</Item>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  cursor: pointer;
`;

const Item = styled.div`
  flex-basis: 30%;
  max-width: 250px;
  min-width: 200px;
`;

const LastUpdateSection = styled.span<{ isLongTimeNoUpdate: boolean | undefined }>`
  color: ${props => (props.isLongTimeNoUpdate ? props.theme.redColor : props.theme.textColor)};
`;

const New = styled.span`
  color: red;
  font-size: 50%;
  padding: 2px 5px;
  border: solid 1px #ff0000;
`;

export default ArchiveTrip;
